/**
 * Furnace / blast furnace / smoker block state: keeps the placed block's lit
 * variant in step with its FurnaceState (lit front, light 13), and gives lit
 * furnaces their flame, smoke and crackle. DOM-free except through the
 * callbacks, so smoke tests can drive it directly.
 *
 * Lighting rides on setBlock: a lit variant is a different block id with
 * light 13, so relightAfterEdit only touches the light around that one cell.
 */
import { furnaceWithLit, isFurnaceBlock, isFurnaceLit, furnaceFacingOf } from '../blocks';
import type { FurnaceWorld } from './furnace';

export interface FurnaceBlockHost {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): unknown;
}

const parse = (key: string): [number, number, number] => {
  const [x, y, z] = key.split(',');
  return [Number(x), Number(y), Number(z)];
};

/** Swap the block at `key` to its lit / unlit twin if it is a furnace and differs. */
export function setFurnaceBlockLit(host: FurnaceBlockHost, key: string, lit: boolean): boolean {
  const [x, y, z] = parse(key);
  const id = host.getBlock(x, y, z);
  if (!isFurnaceBlock(id) || isFurnaceLit(id) === lit) return false;
  host.setBlock(x, y, z, furnaceWithLit(id, lit));
  return true;
}

/** Fix every loaded furnace block whose lit variant disagrees with its state (world load, chunk streaming). */
export function reconcileFurnaceBlocks(host: FurnaceBlockHost, furnaces: FurnaceWorld): number {
  let n = 0;
  for (const [key, st] of furnaces.entries()) if (setFurnaceBlockLit(host, key, st.lit)) n++;
  return n;
}

/** Flame and smoke at the front of every lit furnace within `range` blocks of (px, pz). */
export function furnaceEffects(
  host: FurnaceBlockHost, furnaces: FurnaceWorld, px: number, pz: number, range: number,
  fx: { flame(x: number, y: number, z: number): void; smoke(x: number, y: number, z: number): void },
): void {
  for (const [key, st] of furnaces.entries()) {
    if (!st.lit) continue;
    const [x, y, z] = parse(key);
    if (Math.abs(x - px) > range || Math.abs(z - pz) > range) continue;
    const id = host.getBlock(x, y, z);
    const f = furnaceFacingOf(id);
    if (f < 0) continue;
    const [dx, dz] = [[0, 1], [1, 0], [0, -1], [-1, 0]][f];
    if (Math.random() < 0.3) {
      const r = Math.random() * 0.6 - 0.3;
      fx.flame(x + 0.5 + dx * 0.52 + (dx === 0 ? r : 0), y + 0.2 + Math.random() * 0.3, z + 0.5 + dz * 0.52 + (dz === 0 ? r : 0));
    }
    if (Math.random() < 0.12) fx.smoke(x + 0.5 + dx * 0.52, y + 0.9, z + 0.5 + dz * 0.52);
  }
}
